import React, { useState, useRef } from 'react';
import { CheckCircle, XCircle, Loader } from 'lucide-react';

/**
 * Text input with a debounced live "is this value already taken?" check —
 * shows a spinner while checking, then a green check / red cross + message.
 *
 * Props:
 *   value       — current value (string)
 *   onChange    — (value: string) => void
 *   checkFn     — (value: string) => Promise<{ data: { available, conflict? } }>
 *   placeholder, label, required
 *   onStatus    — (status: 'idle'|'checking'|'ok'|'taken') => void, for parent submit-blocking
 */
export default function UniqueField({ value, onChange, checkFn, placeholder, label, required, onStatus }) {
  const [check, setCheckState] = useState({ status: 'idle', message: '' });
  const debounce = useRef(null);

  const setCheck = (c) => { setCheckState(c); onStatus?.(c.status); };

  const handleChange = (val) => {
    onChange(val);
    setCheck({ status: 'idle', message: '' });
    clearTimeout(debounce.current);
    if (!val.trim()) return;
    setCheck({ status: 'checking', message: '' });
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await checkFn(val.trim());
        if (data.available) {
          setCheck({ status: 'ok', message: 'Available' });
        } else {
          const c = data.conflict;
          setCheck({ status: 'taken', message: `Used by ${c?.name || 'another customer'} (${c?.customerId || ''})` });
        }
      } catch { setCheck({ status: 'idle', message: '' }); }
    }, 600);
  };

  return (
    <div className="form-group">
      {label && <label className="form-label">{label} {required && <span className="required">*</span>}</label>}
      <div style={{ position: 'relative' }}>
        <input
          className="form-control font-mono"
          value={value}
          onChange={e => handleChange(e.target.value)}
          placeholder={placeholder}
          style={{
            paddingRight: 36,
            borderColor: check.status === 'taken' ? 'var(--cb-ef4444)' : check.status === 'ok' ? 'var(--cb-10b981)' : undefined,
          }}
        />
        <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
          {check.status === 'checking' && <Loader size={14} color="var(--c-9ca3af)" />}
          {check.status === 'ok'       && <CheckCircle size={14} color="var(--c-10b981)" />}
          {check.status === 'taken'    && <XCircle size={14} color="var(--c-ef4444)" />}
        </span>
      </div>
      {check.status === 'ok'    && <p style={{ fontSize: '0.75rem', color: 'var(--c-10b981)', marginTop: 3 }}>{check.message}</p>}
      {check.status === 'taken' && <p style={{ fontSize: '0.75rem', color: 'var(--c-ef4444)', marginTop: 3 }}>{check.message}</p>}
    </div>
  );
}
